// src/models/earnPointsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { promotionService } from '../../services/promotionService';


interface EarnPointsRule {
  redeemed: boolean;
  points: string;
  url: string;
}

interface EarnPointsState {
  earnPointsRules: { [key: string]: EarnPointsRule };
  isLoading: boolean;
  error: string | null;
}

const initialState: EarnPointsState = {
  earnPointsRules: {},
  isLoading: false,
  error: null,
};

// In your earnPointsSlice.ts
export const fetchEarnPointsRules = createAsyncThunk(
  'earnPoints/fetchEarnPointsRules',
  async (_, { rejectWithValue }) => {
    try {
      const data = await promotionService.getEarnPointsRules();
      return data; // This will be the data from the service
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch points rules');
    }
  }
);

export const redeemPoints = createAsyncThunk(
  'earnPoints/redeemPoints',
  async (type: string, { rejectWithValue }) => {
    try {
      const data = await promotionService.calculatePointsRule(type);
      return { type, data };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to redeem points');
    }
  }
);

const earnPointsSlice = createSlice({
  name: 'earnPoints',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch earn points rules
      .addCase(fetchEarnPointsRules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEarnPointsRules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.earnPointsRules = action.payload;
      })
      .addCase(fetchEarnPointsRules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Redeem points
      .addCase(redeemPoints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(redeemPoints.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the specific rule to mark as redeemed
        if (state.earnPointsRules[action.payload.type]) {
          state.earnPointsRules[action.payload.type].redeemed = true;
        }
      })
      .addCase(redeemPoints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = earnPointsSlice.actions;
export default earnPointsSlice.reducer;